import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";
import ptBrLocale from "@fullcalendar/core/locales/pt-br";
import dayjs from "dayjs";
import agendaExcecoes from "../api/agendaExcecoes";
import horariosAtendimento from "../api/horariosAtendimento";
import { configuracoes } from "../api/configuracoes";
import {
  buildExcecaoCalendarEvents,
  indexarExcecoesPorData,
  listarMesesNoIntervalo,
  obterBloqueioDoSlot,
  slotDentroHorarioEspecial,
} from "../utils/agendaExcecoesCalendar";
import {
  buildBackgroundEventsDiasFechados,
  buildBusinessHoursFromFuncionamento,
  buildDisponibilidadePorDia,
  buildFaixaDisponibilidadePorDia,
  computeCalendarBounds,
  DEFAULT_SLOT_BOUNDS,
  parseHorariosFuncionamento,
  slotEstaForaDaFaixaVisual,
  slotForaDoExpediente,
} from "../utils/horariosFuncionamentoCalendar";
import { useIsMobile } from "../hooks/useMediaQuery";

const statusColors = {
  pendente: { background: "#f59e0b", border: "#d97706", text: "#111827" },
  confirmado: { background: "#16a34a", border: "#15803d", text: "#ffffff" },
  cancelado: { background: "#ef4444", border: "#dc2626", text: "#ffffff" },
  finalizado: { background: "#64748b", border: "#475569", text: "#ffffff" },
};

const statusLabels = {
  pendente: "Pendente",
  confirmado: "Confirmado",
  cancelado: "Cancelado",
  finalizado: "Finalizado",
};

const DEFAULT_DURACAO_MINUTOS = 30;
const SLOT_DURATION_MINUTES = 15;

const normalizeAppointment = (appointment) =>
  appointment && typeof appointment === "object" ? appointment : {};

const toSafeText = (value, fallback = "") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text || fallback;
};

const getAppointmentDate = (appointment) => {
  const item = normalizeAppointment(appointment);
  return item?.data_horario || item?.data || item?.inicio || item?.start || "";
};

const getEventParts = (appointment, fallbackTitle = "Agendamento") => {
  const item = normalizeAppointment(appointment);

  return {
    cliente: toSafeText(
      item?.nome_cliente ??
        item?.cliente?.nome ??
        item?.cliente_nome ??
        item?.nome,
      fallbackTitle
    ),
    servico: toSafeText(
      item?.servico?.nome ?? item?.nome_servico ?? item?.servico_nome,
      "Serviço"
    ),
    profissional: toSafeText(
      item?.profissional?.nome ??
        item?.nome_profissional ??
        item?.profissional_nome
    ),
  };
};

const buildTitle = (appointment) => {
  const { cliente, servico, profissional } = getEventParts(appointment);
  return profissional
    ? `${cliente} - ${servico} (${profissional})`
    : `${cliente} - ${servico}`;
};

const isValidAppointment = (appointment) => {
  const appointmentDate = getAppointmentDate(appointment);
  return Boolean(
    appointment &&
      typeof appointment === "object" &&
      appointmentDate &&
      !Number.isNaN(new Date(appointmentDate).getTime())
  );
};

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const formatSlotTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);
};

const formatTimeRange = (startValue, endValue) => {
  const inicio = formatSlotTime(startValue);
  const fim = formatSlotTime(endValue);
  if (!inicio) return "";
  return fim ? `${inicio} - ${fim}` : inicio;
};

const getServiceDurationMinutes = (appointment) => {
  const item = normalizeAppointment(appointment);
  const candidates = [
    item?.servico?.duracao_minutos,
    item?.duracao_minutos,
    item?.servico_duracao_minutos,
  ];

  for (const value of candidates) {
    const parsed = Number(value);
    if (Number.isFinite(parsed) && parsed > 0) return parsed;
  }

  return DEFAULT_DURACAO_MINUTOS;
};

const addMinutes = (dateValue, minutes) => {
  const start = new Date(dateValue);
  if (Number.isNaN(start.getTime())) return null;
  return new Date(start.getTime() + minutes * 60 * 1000);
};

const AgendamentosCalendar = ({
  agendamentos = [],
  estabelecimentoId = null,
  mostrarEncerrados = false,
  onMostrarEncerradosChange,
  onEventClick,
  onDateSelect,
  onBlockedSlotClick,
}) => {
  const [excecoesAgenda, setExcecoesAgenda] = useState([]);
  const [horariosFuncionamento, setHorariosFuncionamento] = useState({});
  const [disponibilidadePorDia, setDisponibilidadePorDia] = useState(new Map());
  const [horariosCarregados, setHorariosCarregados] = useState(false);
  const [intervaloVisivel, setIntervaloVisivel] = useState(null);
  const excecoesRequestRef = useRef(0);
  const calendarRef = useRef(null);
  const isMobile = useIsMobile();

  const headerToolbar = useMemo(
    () =>
      isMobile
        ? {
            left: "prev,next today",
            center: "title",
            right: "listWeek,timeGridDay",
          }
        : {
            left: "prev,next today",
            center: "title",
            right: "timeGridDay,timeGridWeek,dayGridMonth,listWeek",
          },
    [isMobile]
  );

  const initialView = isMobile ? "listWeek" : "timeGridWeek";

  useEffect(() => {
    const api = calendarRef.current?.getApi?.();
    if (!api) return;

    const targetView = isMobile ? "listWeek" : "timeGridWeek";
    if (api.view.type !== targetView) {
      api.changeView(targetView);
    }
  }, [isMobile]);

  const calendarBounds = useMemo(
    () =>
      horariosCarregados
        ? computeCalendarBounds(disponibilidadePorDia, horariosFuncionamento)
        : DEFAULT_SLOT_BOUNDS,
    [horariosCarregados, disponibilidadePorDia, horariosFuncionamento]
  );

  const faixaDisponibilidadePorDia = useMemo(
    () => buildFaixaDisponibilidadePorDia(disponibilidadePorDia),
    [disponibilidadePorDia]
  );

  const businessHours = useMemo(() => {
    if (!horariosCarregados) return [];
    return buildBusinessHoursFromFuncionamento(horariosFuncionamento);
  }, [horariosCarregados, horariosFuncionamento]);

  const { slotMinTime, slotMaxTime } = calendarBounds;

  const funcionamentoBackgroundEvents = useMemo(() => {
    if (!horariosCarregados || !intervaloVisivel) return [];
    return buildBackgroundEventsDiasFechados(
      horariosFuncionamento,
      intervaloVisivel,
      { slotMinTime, slotMaxTime }
    );
  }, [
    horariosCarregados,
    horariosFuncionamento,
    intervaloVisivel,
    slotMinTime,
    slotMaxTime,
  ]);

  const carregarHorariosEstabelecimento = useCallback(async () => {
    if (!estabelecimentoId) {
      setHorariosFuncionamento({});
      setDisponibilidadePorDia(new Map());
      setHorariosCarregados(false);
      return;
    }

    try {
      const [funcionamentoResp, disponibilidadeResp] = await Promise.all([
        configuracoes.obterHorarios(estabelecimentoId),
        horariosAtendimento.listar(estabelecimentoId),
      ]);

      setHorariosFuncionamento(parseHorariosFuncionamento(funcionamentoResp.data));
      setDisponibilidadePorDia(
        buildDisponibilidadePorDia(disponibilidadeResp?.horarios || {})
      );
      setHorariosCarregados(true);
    } catch (error) {
      console.error("Erro ao carregar horários do estabelecimento:", error);
      setHorariosFuncionamento({});
      setDisponibilidadePorDia(new Map());
      setHorariosCarregados(false);
    }
  }, [estabelecimentoId]);

  useEffect(() => {
    carregarHorariosEstabelecimento();
  }, [carregarHorariosEstabelecimento]);

  useEffect(() => {
    const recarregar = () => carregarHorariosEstabelecimento();
    window.addEventListener("focus", recarregar);
    return () => window.removeEventListener("focus", recarregar);
  }, [carregarHorariosEstabelecimento]);

  const excecoesPorData = useMemo(
    () => indexarExcecoesPorData(excecoesAgenda),
    [excecoesAgenda]
  );

  const carregarExcecoesVisiveis = useCallback(
    async (intervalo) => {
      if (!estabelecimentoId || !intervalo?.start || !intervalo?.end) {
        setExcecoesAgenda([]);
        return;
      }

      const requestId = excecoesRequestRef.current + 1;
      excecoesRequestRef.current = requestId;
      const meses = listarMesesNoIntervalo(intervalo.start, intervalo.end);

      try {
        const respostas = await Promise.all(
          meses.map((mes) => agendaExcecoes.listar(estabelecimentoId, mes))
        );

        if (excecoesRequestRef.current !== requestId) return;

        const mapa = new Map();
        respostas.forEach((resposta) => {
          (Array.isArray(resposta?.excecoes) ? resposta.excecoes : []).forEach((item) => {
            if (item?.id != null) mapa.set(item.id, item);
          });
        });

        setExcecoesAgenda([...mapa.values()]);
      } catch (error) {
        if (excecoesRequestRef.current !== requestId) return;
        console.error("Erro ao carregar exceções de agenda:", error);
        setExcecoesAgenda([]);
      }
    },
    [estabelecimentoId]
  );

  const { allDayEvents, backgroundEvents } = useMemo(
    () =>
      buildExcecaoCalendarEvents(excecoesAgenda, {
        slotMinTime,
        slotMaxTime,
      }),
    [excecoesAgenda, slotMinTime, slotMaxTime]
  );

  const events = useMemo(
    () =>
      (Array.isArray(agendamentos) ? agendamentos : [])
        .map(normalizeAppointment)
        .filter(isValidAppointment)
        .map((appointment) => {
          const status = String(appointment?.status || "pendente").toLowerCase();
          const colors = statusColors[status] || statusColors.pendente;
          const dataHorario = getAppointmentDate(appointment);
          const duracaoMinutos = getServiceDurationMinutes(appointment);
          const fimHorario = addMinutes(dataHorario, duracaoMinutos);

          return {
            id: String(appointment?.id ?? dataHorario),
            title: buildTitle(appointment),
            start: dataHorario,
            end: fimHorario ? fimHorario.toISOString() : undefined,
            allDay: false,
            backgroundColor: colors.background,
            borderColor: colors.border,
            textColor: colors.text,
            extendedProps: {
              agendamento: appointment,
              duracaoMinutos,
            },
          };
        }),
    [agendamentos]
  );

  const calendarEvents = useMemo(
    () => [
      ...allDayEvents,
      ...funcionamentoBackgroundEvents,
      ...backgroundEvents,
      ...events,
    ],
    [allDayEvents, funcionamentoBackgroundEvents, backgroundEvents, events]
  );

  const verificarBloqueio = useCallback(
    (dateValue) => {
      const bloqueioExcecao = obterBloqueioDoSlot(
        dateValue,
        excecoesPorData,
        slotMinTime,
        slotMaxTime
      );
      if (bloqueioExcecao?.message) return bloqueioExcecao;

      if (slotDentroHorarioEspecial(dateValue, excecoesPorData)) {
        return null;
      }

      if (!horariosCarregados) return null;

      const expediente = slotForaDoExpediente(dateValue, horariosFuncionamento);
      if (expediente.bloqueado) {
        return { message: expediente.message };
      }

      return null;
    },
    [
      excecoesPorData,
      slotMinTime,
      slotMaxTime,
      horariosCarregados,
      horariosFuncionamento,
    ]
  );

  const slotIndisponivelVisual = useCallback(
    (dateValue) => {
      const bloqueio = verificarBloqueio(dateValue);
      if (bloqueio?.message) return bloqueio;

      if (
        horariosCarregados &&
        slotEstaForaDaFaixaVisual(dateValue, faixaDisponibilidadePorDia)
      ) {
        return { message: "Fora da grade de disponibilidade deste dia." };
      }

      return null;
    },
    [verificarBloqueio, horariosCarregados, faixaDisponibilidadePorDia]
  );

  const renderEventContent = (eventInfo) => {
    const event = eventInfo?.event;
    if (!event || event.display === "background") return null;

    if (event.extendedProps?.isExcecaoAgenda) {
      return (
        <div className="fc-fz-excecao">
          <span className="fc-fz-excecao__label">{event.title}</span>
        </div>
      );
    }

    const appointment = normalizeAppointment(event.extendedProps?.agendamento);
    const fallbackTitle = toSafeText(eventInfo?.event?.title, "Agendamento");
    const { cliente, servico, profissional } = getEventParts(
      appointment,
      fallbackTitle
    );
    const horario =
      formatTimeRange(eventInfo?.event?.start, eventInfo?.event?.end) ||
      eventInfo?.timeText;

    return (
      <div className="fc-fz-event">
        {horario && <span className="fc-fz-event__time">{horario}</span>}
        <span className="fc-fz-event__client">{cliente}</span>
        <span className="fc-fz-event__service">
          {servico}
          {profissional ? ` - ${profissional}` : ""}
        </span>
      </div>
    );
  };

  return (
    <div className={`calendar-shell${isMobile ? " calendar-shell--mobile" : ""}`}>
      <div className="calendar-shell__header">
        <div>
          <strong>Calendário de agendamentos</strong>
          <span>
            Os blocos respeitam a duração do serviço. Clique em um horário dentro
            do expediente para criar um agendamento.
          </span>
        </div>

        <label className="calendar-show-closed">
          <input
            type="checkbox"
            checked={mostrarEncerrados}
            onChange={(event) => onMostrarEncerradosChange?.(event.target.checked)}
          />
          <span>Mostrar cancelados e finalizados</span>
        </label>

        <div className="calendar-legend" aria-label="Legenda de status">
          {Object.entries(statusColors).map(([status, colors]) => (
            <span key={status}>
              <i style={{ backgroundColor: colors.background }} />
              {statusLabels[status]}
            </span>
          ))}
          <span>
            <i style={{ backgroundColor: "#e2e8f0" }} />
            Indisponível
          </span>
        </div>
      </div>

      <FullCalendar
        ref={calendarRef}
        plugins={[
          dayGridPlugin,
          timeGridPlugin,
          interactionPlugin,
          listPlugin,
        ]}
        locale={ptBrLocale}
        initialView={initialView}
        headerToolbar={headerToolbar}
        buttonText={{
          today: "Hoje",
          day: "Dia",
          week: "Semana",
          month: "Mês",
          list: "Lista",
        }}
        events={calendarEvents}
        nowIndicator
        datesSet={(info) => {
          setIntervaloVisivel({ start: info.start, end: info.end });
          carregarExcecoesVisiveis(info);
        }}
        dateClick={(info) => {
          if (info.jsEvent?.target?.closest?.(".fc-event")) return;

          const alvoData = info.allDay
            ? dayjs(info.date).hour(12).minute(0).second(0).toDate()
            : info.date;
          const bloqueio = verificarBloqueio(alvoData);

          if (bloqueio?.message) {
            onBlockedSlotClick?.(bloqueio.message);
            return;
          }

          if (info.allDay) return;

          onDateSelect?.({
            start: info.date,
            end: null,
            allDay: info.allDay,
            view: info.view,
          });
        }}
        eventClick={(info) => {
          const evento = info?.event;
          if (
            evento?.display === "background" ||
            evento?.extendedProps?.isExcecaoAgenda
          ) {
            const mensagem =
              evento.extendedProps?.blockReason || evento.title || "";
            if (mensagem) onBlockedSlotClick?.(mensagem);
            return;
          }

          const appointment = normalizeAppointment(
            info?.event?.extendedProps?.agendamento
          );
          if (Object.keys(appointment).length) onEventClick?.(appointment);
        }}
        eventContent={renderEventContent}
        eventDidMount={(info) => {
          if (!info?.el || !info?.event) return;

          if (info.event.display === "background") {
            info.el.title = info.event.extendedProps?.blockReason || "";
            info.el.style.cursor = "not-allowed";
            return;
          }

          if (info.event.extendedProps?.isExcecaoAgenda) {
            info.el.title =
              info.event.extendedProps?.blockReason || info.event.title || "";
            info.el.style.cursor = "not-allowed";
            return;
          }

          const appointment = normalizeAppointment(
            info?.event?.extendedProps?.agendamento
          );
          const fallbackTitle = toSafeText(info?.event?.title, "Agendamento");
          const { cliente, servico, profissional } = getEventParts(
            appointment,
            fallbackTitle
          );
          const statusKey = String(
            appointment?.status || "pendente"
          ).toLowerCase();
          const status = statusLabels[statusKey] || "Pendente";
          const inicio = getAppointmentDate(appointment) || info?.event?.start;
          const duracaoMinutos =
            info?.event?.extendedProps?.duracaoMinutos ??
            getServiceDurationMinutes(appointment);
          const fim = info?.event?.end || addMinutes(inicio, duracaoMinutos);
          const horario = formatTimeRange(inicio, fim);
          const dataHora = formatDateTime(inicio);

          info.el.title = `${
            horario || dataHora ? `${horario || dataHora} (${duracaoMinutos} min) - ` : ""
          }${cliente} - ${servico}${
            profissional ? ` (${profissional})` : ""
          } - ${status}`;
        }}
        slotLaneDidMount={(info) => {
          if (!info?.el || !info?.date) return;

          const horario = formatSlotTime(info.date);
          if (!horario) return;

          const bloqueio = verificarBloqueio(info.date);
          const indisponivelVisual = slotIndisponivelVisual(info.date);
          info.el.dataset.slotTime = horario;

          if (bloqueio?.message) {
            info.el.classList.add("fc-fz-unavailable");
            info.el.classList.remove("fc-fz-outside-slots");
            info.el.dataset.unavailable = "true";
            info.el.style.cursor = "not-allowed";
            info.el.title = bloqueio.message;
            return;
          }

          if (indisponivelVisual?.message) {
            info.el.classList.add("fc-fz-outside-slots");
            info.el.classList.remove("fc-fz-unavailable");
            delete info.el.dataset.unavailable;
            info.el.style.cursor = "pointer";
            info.el.title = `Criar agendamento às ${horario}`;
            return;
          }

          info.el.classList.remove("fc-fz-unavailable", "fc-fz-outside-slots");
          delete info.el.dataset.unavailable;
          info.el.style.cursor = "pointer";
          info.el.title = `Criar agendamento às ${horario}`;
        }}
        height="auto"
        expandRows
        firstDay={0}
        allDayText="Dia inteiro"
        slotMinTime={`${slotMinTime}:00`}
        slotMaxTime={`${slotMaxTime}:00`}
        slotDuration={`00:${String(SLOT_DURATION_MINUTES).padStart(2, "0")}:00`}
        slotLabelInterval="01:00:00"
        snapDuration={`00:${String(SLOT_DURATION_MINUTES).padStart(2, "0")}:00`}
        defaultTimedEventDuration="00:30:00"
        displayEventEnd
        slotLabelFormat={{
          hour: "2-digit",
          minute: "2-digit",
          meridiem: false,
        }}
        eventTimeFormat={{
          hour: "2-digit",
          minute: "2-digit",
          meridiem: false,
        }}
        dayMaxEvents={4}
        businessHours={businessHours}
      />
    </div>
  );
};

export default AgendamentosCalendar;
